'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { useLandingProductGTM } from '@/fo/api/gtm';
import { UiKitPriceBase } from '@/fo/ui/kit/price/base';
import { MappedProduct } from '@/fo/util/map-product-data';
import { reachMetrikaGoal } from '@/fo/util/metrics';

import { styles } from './UiLandingProductCardEsthederm.styles';

export interface Product {
  image?: {
    sm?: string;
    md?: string;
  };
  name?: string;
  label?: ReactNode;
  refill?: {
    image: string;
    title: ReactNode;
    value: ReactNode;
    text: ReactNode;
  };
  claims?: {
    value?: ReactNode;
    unit?: ReactNode;
    text?: ReactNode;
    image?: string;
    accent?: boolean;
  }[];
  heading?: ReactNode;
  text?: ReactNode;
  description?: ReactNode;
  listTitle?: ReactNode;
  list?: ReactNode[];
  basePrice?: number;
  finalPrice?: number | null;
  sku?: string;
  brand?: string;
  series?: string;
  link?: string;
  volume?: string;
  baseImage?: { src: string; alt: string } | null;
  configurableVariants?: MappedProduct['configurableVariants'];
}

export interface UiLandingProductCardEsthedermProps {
  variant?: 'default' | 'secondary';
  onAddToCart?: () => void;
  product?: Product;
  positionIndex?: number;
}

export function UiLandingProductCardEsthederm({
  variant = 'default',
  onAddToCart,
  product,
  positionIndex = 0
}: UiLandingProductCardEsthedermProps) {
  const slots = styles({ variant });
  const t = useTranslations();
  const { ref, onSelectItem, viewItemList } = useLandingProductGTM(
    product,
    positionIndex
  );

  return (
    <div
      ref={ref}
      className={slots.base()}
    >
      {viewItemList}
      <div className={slots.content()}>
        {variant === 'secondary' && product?.image?.md && (
          <img
            className={slots.image()}
            src={product.image.md}
            alt="product-card-bg"
          />
        )}
        <p className={slots.title()}>
          {product?.heading && (
            <>
              <span className={slots.heading()}>{product.heading}</span>&nbsp;
            </>
          )}
          {product?.text && (
            <span className={slots.text()}>{product.text}</span>
          )}
        </p>
        {product?.description && (
          <p className={slots.description()}>{product.description}</p>
        )}
        {product?.listTitle && (
          <p className={slots.listTitle()}>{product.listTitle}</p>
        )}
        {product?.list && (
          <ul className={slots.list()}>
            {product?.list?.map((item, index) => (
              <li
                key={index}
                className={slots.listItem()}
              >
                {item}
              </li>
            ))}
          </ul>
        )}
      </div>
      {product?.basePrice && (
        <UiKitPriceBase
          value={product.finalPrice || product.basePrice || 0}
          oldValue={product.basePrice}
          className={slots.price()}
          classNameValue={slots.priceValue()}
          classNameOldValue={slots.oldPrice()}
        />
      )}

      <div className={slots.buttons()}>
        {product?.basePrice && (
          <button
            className={slots.button({ color: 'default' })}
            onClick={() => {
              onAddToCart?.();
              reachMetrikaGoal('dobavlenie_v_korzinu');
            }}
          >
            {t('To cart')}
          </button>
        )}
        {product?.link && (
          <Link
            href={product.link}
            target="_blank"
            className={slots.button({ color: 'secondary' })}
            onClick={() => {
              onSelectItem();
              reachMetrikaGoal('perehod_v_kartochku_tovara');
            }}
          >
            {t('more')}
          </Link>
        )}
      </div>
    </div>
  );
}
